import type { MRT_ColumnDef } from "mantine-react-table";

const DEFAULT_FALLBACK_LABEL = "N/A";

const isNumericSegment = (segment: string): boolean => /^\d+$/.test(segment.trim());

const normaliseFieldPath = (field: string): string => field.replace(/^metadata\./, "");

export const buildColumnId = (namespace: string, field: string): string => `${namespace}__${field}`;

export const buildAccessorKey = (field: string): string => {
    const normalised = normaliseFieldPath(field);
    return normalised ? `fields.${normalised}` : "fields";
};

export type MetadataRowLike = {
    fields?: Record<string, unknown>;
    _source?: {
        metadata?: Record<string, unknown>;
        [key: string]: unknown;
    };
    [key: string]: unknown;
};

export const getMetadataValue = <T extends MetadataRowLike>(row: T, field: string): unknown => {
    const metadata =
        // biome-ignore lint/suspicious/noExplicitAny: dynamic metadata structure from API
        (row as any)?.fields ?? (row as any)?._source?.metadata ?? (row as any)?._source ?? {};

    const normalised = normaliseFieldPath(field);
    if (!normalised) return metadata;

    const segments = normalised.split(".");
    let currentValues: unknown[] = [metadata];

    for (let i = 0; i < segments.length; i++) {
        const segment = segments[i];
        const nextValues: unknown[] = [];

        for (const item of currentValues) {
            if (item === null || item === undefined) continue;

            // Explicit Index Access: e.g., links.0.Machine No
            if (Array.isArray(item) && isNumericSegment(segment)) {
                const indexedValue = item[Number(segment)];
                if (indexedValue !== undefined) nextValues.push(indexedValue);
                continue;
            }

            // Collection Mapping: If we are at an array but the segment is a property name
            if (Array.isArray(item)) {
                for (const subItem of item) {
                    const value = (subItem as Record<string, unknown>)?.[segment];
                    if (value !== undefined) nextValues.push(value);
                }
                continue;
            }

            // Standard Object Access
            const value = (item as Record<string, unknown>)?.[segment];
            if (value !== undefined) nextValues.push(value);
        }

        currentValues = nextValues;
    }

    // Filter nulls/undefined and remove duplicates
    const finalResults = currentValues.filter((v) => v !== null && v !== undefined);
    const uniqueResults = Array.from(new Set(finalResults));

    if (uniqueResults.length === 0) return undefined;

    // If we only have one result, return it directly; otherwise return the unique array
    return uniqueResults.length === 1 ? uniqueResults[0] : uniqueResults;
};

export const parseDate = (value: unknown): Date | undefined => {
    if (!value) return undefined;

    // Handle Unix timestamps: if it's a number with ~10 digits, it's in seconds and needs conversion to milliseconds
    let timestamp = value;
    if (typeof value === "number" && value < 10000000000) {
        timestamp = value * 1000; // Convert seconds to milliseconds
    }

    const date =
        timestamp instanceof Date ? new Date(timestamp) : new Date(timestamp as string | number);
    if (Number.isNaN(date.getTime())) return undefined;
    date.setHours(0, 0, 0, 0);
    return date;
};

export const formatDateForCell = (
    value: Date | undefined,
    fallbackLabel = DEFAULT_FALLBACK_LABEL,
) => {
    if (!value) return fallbackLabel;
    return value.toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
    });
};

type ColumnOverrides<T extends MetadataRowLike> = Partial<MRT_ColumnDef<T>>;

interface BaseColumnOptions<T extends MetadataRowLike> {
    namespace: string;
    field: string;
    header: string;
    fallback?: string;
    overrides?: ColumnOverrides<T>;
    accessor?: (row: T) => unknown;
}

export const createStringColumn = <T extends MetadataRowLike>(
    options: BaseColumnOptions<T>,
): MRT_ColumnDef<T> => {
    const {
        namespace,
        field,
        header,
        fallback = DEFAULT_FALLBACK_LABEL,
        accessor,
        overrides,
    } = options;

    return {
        id: buildColumnId(namespace, field),
        header,
        accessorKey: buildAccessorKey(field),
        accessorFn: (row) => {
            const value = accessor ? accessor(row) : getMetadataValue(row, field);
            if (value === undefined || value === null || value === "") {
                return fallback;
            }
            return value;
        },
        filterFn: "includesString",
        ...overrides,
    } satisfies MRT_ColumnDef<T>;
};

interface DateColumnOptions<T extends MetadataRowLike>
    extends Omit<BaseColumnOptions<T>, "fallback"> {
    fallbackLabel?: string;
}

export const createDateColumn = <T extends MetadataRowLike>(
    options: DateColumnOptions<T>,
): MRT_ColumnDef<T> => {
    const {
        namespace,
        field,
        header,
        accessor,
        overrides,
        fallbackLabel = DEFAULT_FALLBACK_LABEL,
    } = options;

    return {
        id: buildColumnId(namespace, field),
        header,
        accessorKey: buildAccessorKey(field),
        accessorFn: (row) => {
            const rawValue = accessor ? accessor(row) : getMetadataValue(row, field);
            return parseDate(rawValue);
        },
        Cell: ({ cell }) => formatDateForCell(cell.getValue<Date | undefined>(), fallbackLabel),
        filterVariant: "date-range",
        sortingFn: "datetime",
        ...overrides,
    } satisfies MRT_ColumnDef<T>;
};

export const normalizeStringArray = (
    value?: string[] | string,
    fallback = DEFAULT_FALLBACK_LABEL,
): string => {
    if (Array.isArray(value)) {
        const joined = value.join(", ");
        return joined || fallback;
    }
    if (typeof value === "string") return value || fallback;
    return fallback;
};
